import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import {
    FilterTasksOffcanvas,
    AddTaskModal,
    MoveOrCopyTaskModal,
    LogTimeModal,
    QuickAddSubtasksModal,
    DeleteTaskModal,
    TasksTable,
    Pagination,
    ViewHistoryModal,
    EstimateTimeModal,
    ContactDetailsModal
} from './components';
import {
    priorityOptions,
    privacyOptions,
    assigneeOptions,
    projectOptions,
    taskListOptions,
    stageOptions
} from './constants/options';

const MyTasks = () => {
    const navigate = useNavigate();
    const [show, setShow] = useState(false);
    const [showDelete, setShowDelete] = useState(false);
    const [showFilter, setShowFilter] = useState(false);
    const [showMoveOrCopy, setShowMoveOrCopy] = useState(false);
    const [showContact, setShowContact] = useState(false);
    const [selectedContact, setSelectedContact] = useState(null);
    const [moveOrCopyType, setMoveOrCopyType] = useState('move'); // 'move' or 'copy'
    const [targetProject, setTargetProject] = useState(null);
    const [targetTaskList, setTargetTaskList] = useState(null);

    // Log Time Modal state
    const [showLogTime, setShowLogTime] = useState(false);
    const [logDate, setLogDate] = useState(new Date());
    const [logTime, setLogTime] = useState('');
    const [logDescription, setLogDescription] = useState('');

    // Pagination state
    const [pageSize, setPageSize] = useState(20);
    const [currentPage, setCurrentPage] = useState(1);
    const totalItems = 100; // This would come from your API
    const totalPages = Math.ceil(totalItems / pageSize);

    // State for filters
    const [taskNameFilter, setTaskNameFilter] = useState('');
    const [projectFilter, setProjectFilter] = useState(null);
    const [taskListFilter, setTaskListFilter] = useState(null);
    const [assigneeFilter, setAssigneeFilter] = useState([]);
    const [dueDateRange, setDueDateRange] = useState([null, null]);
    const [priorityFilter, setPriorityFilter] = useState(null);
    const [stageFilter, setStageFilter] = useState(null);

    // State for new task form
    const [selectedProject, setSelectedProject] = useState(null);
    const [selectedTaskList, setSelectedTaskList] = useState(null);
    const [startDate, setStartDate] = useState(new Date());
    const [dueDate, setDueDate] = useState(new Date());
    const [estimatedTime, setEstimatedTime] = useState('');
    const [selectedPriority, setSelectedPriority] = useState(null);
    const [selectedPrivacy, setSelectedPrivacy] = useState(null);
    const [selectedAssignees, setSelectedAssignees] = useState([]);
    const [description, setDescription] = useState('');
    const [notifyByEmail, setNotifyByEmail] = useState(false);

    const handleClose = () => {
        setShow(false);
        setSelectedProject(null);
        setSelectedTaskList(null);
        setStartDate(new Date());
        setDueDate(new Date());
        setEstimatedTime('');
        setSelectedPriority(null);
        setSelectedPrivacy(null);
        setSelectedAssignees([]);
        setDescription('');
        setNotifyByEmail(false);
    };
    const handleShow = () => setShow(true);

    const handleDeleteClose = () => setShowDelete(false);
    const handleDeleteShow = () => setShowDelete(true);
    
    const handleDelete = () => {
        handleDeleteClose();
    };

    const handleView = (taskId) => {
        navigate(`/tasks/detail/${taskId || 'TASK-001'}`);
    };

    const handleMoveOrCopyShow = () => {
        setShowMoveOrCopy(true);
    };

    const handleMoveOrCopyClose = () => {
        setShowMoveOrCopy(false);
        setMoveOrCopyType('move');
        setTargetProject(null);
        setTargetTaskList(null);
    };

    const handleContactShow = (contact) => {
        setSelectedContact(contact);
        setShowContact(true);
    };

    const handleContactClose = () => {
        setShowContact(false);
        setSelectedContact(null);
    };

    const handleMoveOrCopySubmit = () => {
        // Handle the move or copy action here
        const targetProjectName = projectOptions.find(p => p.value === targetProject)?.label || 'selected project';
        const targetListName = taskListOptions.find(t => t.value === targetTaskList)?.label || 'selected list';
        
        setHistoryList([
            {
                date: new Date().toLocaleDateString(),
                time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                user: 'Current User',
                action: `${moveOrCopyType === 'move' ? 'Moved' : 'Copied'} task to "${targetProjectName}" - "${targetListName}"`,
                type: moveOrCopyType
            },
            ...historyList
        ]);
        handleMoveOrCopyClose();
    };

    const handleLogTimeShow = () => {
        setShowLogTime(true);
    };

    const handleLogTimeClose = () => {
        setShowLogTime(false);
        setLogDate(new Date());
        setLogTime('');
        setLogDescription('');
    };

    const handleLogTimeSubmit = () => {
        // Handle the log time submission here
        setHistoryList([
            {
                date: new Date().toLocaleDateString(),
                time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                user: 'Current User',
                action: `Logged ${logTime} hours${logDescription ? `: ${logDescription}` : ''}`,
                type: 'time'
            },
            ...historyList
        ]);
        handleLogTimeClose();
    };

    const [showQuickSubtasks, setShowQuickSubtasks] = useState(false);
    const [subtasksList, setSubtasksList] = useState('');
    const [showHistory, setShowHistory] = useState(false);
    const [historyTab, setHistoryTab] = useState('all');
    const [historyList, setHistoryList] = useState([
        {
            date: '12 Aug 2025',
            time: '10:30 AM',
            user: 'John Doe',
            action: 'Changed status from "In Progress" to "Completed"',
            type: 'status'
        },
        {
            date: '12 Aug 2025',
            time: '09:45 AM',
            user: 'John Doe',
            action: 'Logged 3 hours: Development of API endpoints',
            type: 'time'
        },
        {
            date: '11 Aug 2025',
            time: '04:30 PM',
            user: 'Jane Smith',
            action: 'Logged 2.5 hours: Code review and documentation',
            type: 'time'
        },
        {
            date: '11 Aug 2025',
            time: '02:15 PM',
            user: 'Jane Smith',
            action: 'Added new subtask "Update API documentation"',
            type: 'subtask'
        },
        {
            date: '11 Aug 2025',
            time: '10:00 AM',
            user: 'Mike Johnson',
            action: 'Logged 4 hours: Frontend implementation',
            type: 'time'
        },
        {
            date: '10 Aug 2025',
            time: '11:45 AM',
            user: 'Mike Johnson',
            action: 'Updated due date to 15 Aug 2025',
            type: 'date'
        },
    ]);
    
    const handleQuickSubtasksShow = () => setShowQuickSubtasks(true);
    const handleHistoryShow = () => {
        // In a real app, this would fetch the history from an API
        setHistoryList([
            {
                date: new Date().toLocaleDateString(),
                time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                user: 'Current User',
                action: 'Viewed task history',
                type: 'view'
            },
            ...historyList
        ]);
        setShowHistory(true);
    };

    const handleHistoryClose = () => setShowHistory(false);
    const handleQuickSubtasksClose = () => {
        setShowQuickSubtasks(false);
        setSubtasksList('');
    };
    
    const handleQuickSubtasksSubmit = () => {
        // Split the text into individual subtasks, filtering out empty lines
        const subtasks = subtasksList
            .split('\n')
            .map(task => task.trim())
            .filter(task => task.length > 0);
            
        if (subtasks.length > 0) {
            // TODO: Implement subtasks creation logic
            // Add to history
            setHistoryList([
                {
                    date: new Date().toLocaleDateString(),
                    time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                    user: 'Current User',
                    action: `Added ${subtasks.length} new subtask${subtasks.length > 1 ? 's' : ''}`,
                    type: 'subtask'
                },
                ...historyList
            ]);
            handleQuickSubtasksClose();
        }
    };

    const [showEstimateTime, setShowEstimateTime] = useState(false);

    const handleEstimateTimeShow = () => setShowEstimateTime(true);

    return (
        <>
            <div className='c-card-1'>
                <div className='c-card-header-1'>
                    <div className='d-flex justify-content-between align-items-center w-100'>
                        <div>
                            Tasks
                        </div>
                        <div className='d-flex align-items-center gap-1'>
                            <Button variant='outline-primary' 
                                className='btn-sm me-2 text-nowrap'
                                onClick={() => setShowFilter(true)}
                            >
                                <i className="fas fa-filter me-1"></i>
                                Filter
                            </Button>
                            <Button variant='primary' onClick={handleShow}
                                className='btn-sm text-nowrap'
                            >
                                <i className="fas fa-plus me-1"></i>
                                Add Tasks
                            </Button>
                        </div>
                    </div>
                </div>
                <div className='c-card-body-1 p-0'>
                    <TasksTable
                        onEdit={handleShow}
                        onMoveOrCopy={handleMoveOrCopyShow}
                        onLogTime={handleLogTimeShow}
                        onViewHistory={handleHistoryShow}
                        onAddSubtasks={handleQuickSubtasksShow}
                        onView={handleView}
                        onDuplicate={() => {}} // TODO: Implement duplicate handler
                        onDelete={handleDeleteShow}
                        onEstimateTime={handleEstimateTimeShow}
                        onContactClick={handleContactShow}
                    />
                </div>
                <div className='c-card-footer-1'>
                    <Pagination
                        pageSize={pageSize}
                        onPageSizeChange={setPageSize}
                        currentPage={currentPage}
                        onPageChange={setCurrentPage}
                        totalPages={totalPages}
                        totalItems={totalItems}
                    />
                </div>
            </div>

            <AddTaskModal
                show={show}
                onHide={handleClose}
                selectedProject={selectedProject}
                setSelectedProject={setSelectedProject}
                selectedTaskList={selectedTaskList}
                setSelectedTaskList={setSelectedTaskList}
                startDate={startDate}
                setStartDate={setStartDate}
                dueDate={dueDate}
                setDueDate={setDueDate}
                estimatedTime={estimatedTime}
                setEstimatedTime={setEstimatedTime}
                selectedPriority={selectedPriority}
                setSelectedPriority={setSelectedPriority}
                selectedPrivacy={selectedPrivacy}
                setSelectedPrivacy={setSelectedPrivacy}
                selectedAssignees={selectedAssignees}
                setSelectedAssignees={setSelectedAssignees}
                description={description}
                setDescription={setDescription}
                notifyByEmail={notifyByEmail}
                setNotifyByEmail={setNotifyByEmail}
                projectOptions={projectOptions}
                taskListOptions={taskListOptions}
                priorityOptions={priorityOptions}
                privacyOptions={privacyOptions}
                assigneeOptions={assigneeOptions}
            />

            <DeleteTaskModal
                show={showDelete}
                onHide={handleDeleteClose}
                onConfirm={handleDelete}
            />

            <LogTimeModal
                show={showLogTime}
                onHide={handleLogTimeClose}
                logDate={logDate}
                setLogDate={setLogDate}
                logTime={logTime}
                setLogTime={setLogTime}
                logDescription={logDescription}
                setLogDescription={setLogDescription}
                onSubmit={handleLogTimeSubmit}
            />

            <MoveOrCopyTaskModal
                show={showMoveOrCopy}
                onHide={handleMoveOrCopyClose}
                moveOrCopyType={moveOrCopyType}
                setMoveOrCopyType={setMoveOrCopyType}
                targetProject={targetProject}
                setTargetProject={setTargetProject}
                targetTaskList={targetTaskList}
                setTargetTaskList={setTargetTaskList}
                projectOptions={projectOptions}
                taskListOptions={taskListOptions}
                onSubmit={handleMoveOrCopySubmit}
            />



            <FilterTasksOffcanvas
                show={showFilter}
                onHide={() => setShowFilter(false)}
                taskNameFilter={taskNameFilter}
                setTaskNameFilter={setTaskNameFilter}
                projectFilter={projectFilter}
                setProjectFilter={setProjectFilter}
                taskListFilter={taskListFilter}
                setTaskListFilter={setTaskListFilter}
                assigneeFilter={assigneeFilter}
                setAssigneeFilter={setAssigneeFilter}
                dueDateRange={dueDateRange}
                setDueDateRange={setDueDateRange}
                priorityFilter={priorityFilter}
                setPriorityFilter={setPriorityFilter}
                stageFilter={stageFilter}
                setStageFilter={setStageFilter}
                projectOptions={projectOptions}
                taskListOptions={taskListOptions}
                assigneeOptions={assigneeOptions}
                priorityOptions={priorityOptions}
                stageOptions={stageOptions}
            />

            <QuickAddSubtasksModal
                show={showQuickSubtasks}
                onHide={handleQuickSubtasksClose}
                subtasksList={subtasksList}
                setSubtasksList={setSubtasksList}
                onSubmit={handleQuickSubtasksSubmit}
            />

            <ViewHistoryModal
                show={showHistory}
                onHide={handleHistoryClose}
                historyTab={historyTab}
                setHistoryTab={setHistoryTab}
                historyList={historyList}
            />

            <EstimateTimeModal
                show={showEstimateTime}
                onHide={() => setShowEstimateTime(false)}
            />

            <ContactDetailsModal
                show={showContact}
                onHide={handleContactClose}
                contact={selectedContact}
            />
        </>
    );
};

export default MyTasks;